/**
 * Offline Action Queue
 * 
 * Implements action queue for user actions performed while offline with
 * automatic replay when online, conflict resolution, and retry logic.
 */

import { indexedDBService } from '@/services/offline/indexeddb';
import type { OfflineAction } from '@/services/offline/indexeddb/schema';

/**
 * Action types that can be queued
 */
export type QueuedActionType = 
  | 'create_training_session'
  | 'update_training_session'
  | 'delete_training_session'
  | 'create_technique'
  | 'update_technique'
  | 'delete_technique'
  | 'save_user_technique'
  | 'remove_user_technique'
  | 'update_user_profile'
  | 'create_game_plan'
  | 'update_game_plan'
  | 'delete_game_plan';

/**
 * Queued action interface
 */
export interface QueuedAction {
  id: string;
  type: QueuedActionType;
  payload: Record<string, unknown>;
  timestamp: number;
  userId: string;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  optimisticId?: string; // For optimistic updates
}

/**
 * Action execution result
 */
export interface ActionExecutionResult {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
  shouldRetry?: boolean;
}

/**
 * Action executor function type
 */
export type ActionExecutor = (action: QueuedAction) => Promise<ActionExecutionResult>;

/**
 * Offline Action Queue Manager
 */
export class OfflineActionQueue {
  private executors: Map<QueuedActionType, ActionExecutor> = new Map();
  private isProcessing = false;
  private processingInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.setupDefaultExecutors();
    this.setupNetworkListeners();
  }

  /**
   * Add action to queue
   */
  async enqueueAction(
    type: QueuedActionType,
    payload: Record<string, unknown>,
    userId: string,
    optimisticId?: string
  ): Promise<string> {
    const action: QueuedAction = {
      id: crypto.randomUUID(),
      type,
      payload,
      timestamp: Date.now(),
      userId,
      retryCount: 0,
      maxRetries: 3,
      status: 'pending',
      optimisticId
    };

    // Store in IndexedDB
    const offlineAction: OfflineAction = {
      id: action.id,
      action_type: action.type,
      data: action as unknown as Record<string, unknown>,
      created_at: new Date().toISOString(),
      status: 'pending'
    };

    await indexedDBService.offlineDB.put('offline_actions', offlineAction);



    // Try to process immediately if online
    if (navigator.onLine) {
      this.processQueue().catch(console.error);
    }

    return action.id;
  }

  /**
   * Register action executor
   */
  registerExecutor(type: QueuedActionType, executor: ActionExecutor): void {
    this.executors.set(type, executor);
  }

  /**
   * Process all pending actions in queue
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing || !navigator.onLine) {
      return;
    }

    this.isProcessing = true;

    try {
      const pendingActions = await this.getPendingActions();


      for (const actionData of pendingActions) {
        try {
          await this.executeAction(actionData.data as unknown as QueuedAction);
        } catch (error) {
          console.error('Failed to execute action:', error);
        }
      }
    } catch (error) {
      console.error('Failed to process action queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Execute a single action
   */
  private async executeAction(action: QueuedAction): Promise<void> {
    const executor = this.executors.get(action.type);
    if (!executor) {
      console.warn(`No executor found for action type: ${action.type}`);
      await this.markActionFailed(action.id, 'No executor found');
      return;
    }

    // Mark as processing
    await this.updateActionStatus(action.id, 'processing');

    try {
      const result = await executor(action);

      if (result.success) {
        await this.markActionCompleted(action.id, result.data);

      } else {
        if (result.shouldRetry && action.retryCount < action.maxRetries) {
          await this.retryAction(action.id, result.error);
        } else {
          await this.markActionFailed(action.id, result.error);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (action.retryCount < action.maxRetries) {
        await this.retryAction(action.id, errorMessage);
      } else {
        await this.markActionFailed(action.id, errorMessage);
      }
    }
  }

  /**
   * Get all pending actions
   */
  private async getPendingActions(): Promise<OfflineAction[]> {
    return indexedDBService.offlineDB.queryByIndex<OfflineAction>(
      'offline_actions',
      'by_status',
      'pending'
    );
  }

  /**
   * Update action status
   */
  private async updateActionStatus(
    actionId: string, 
    status: QueuedAction['status']
  ): Promise<void> {
    const action = await indexedDBService.offlineDB.get<OfflineAction>('offline_actions', actionId);
    if (action) {
      action.status = status;
      action.data.status = status;
      await indexedDBService.offlineDB.put('offline_actions', action);
    }
  }

  /**
   * Mark action as completed
   */
  private async markActionCompleted(actionId: string, result?: Record<string, unknown>): Promise<void> {
    const action = await indexedDBService.offlineDB.get<OfflineAction>('offline_actions', actionId);
    if (action) {
      action.status = 'completed';
      action.data.status = 'completed';
      action.data.result = result;
      await indexedDBService.offlineDB.put('offline_actions', action);
    }
  }

  /**
   * Mark action as failed
   */
  private async markActionFailed(actionId: string, error?: string): Promise<void> {
    const action = await indexedDBService.offlineDB.get<OfflineAction>('offline_actions', actionId);
    if (action) {
      action.status = 'failed';
      action.data.status = 'failed';
      action.data.error = error;
      action.error_message = error;
      await indexedDBService.offlineDB.put('offline_actions', action);
    }
    
    console.error(`❌ Failed offline action: ${actionId}`, error);
  }

  /**
   * Retry action
   */
  private async retryAction(actionId: string, error?: string): Promise<void> {
    const action = await indexedDBService.offlineDB.get<OfflineAction>('offline_actions', actionId);
    if (action) {
      (action.data as { retryCount: number }).retryCount++;
      action.data.error = error;
      action.status = 'pending';
      action.data.status = 'pending';
      await indexedDBService.offlineDB.put('offline_actions', action);
    }
    
    console.warn(`🔄 Retrying offline action: ${actionId} (attempt ${action?.data.retryCount})`);
  }

  /**
   * Clear completed actions
   */
  async clearCompletedActions(): Promise<number> {
    const completedActions = await indexedDBService.offlineDB.queryByIndex<OfflineAction>(
      'offline_actions',
      'by_status',
      'completed'
    );

    for (const action of completedActions) {
      await indexedDBService.offlineDB.delete('offline_actions', action.id);
    }


    return completedActions.length;
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<{
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  }> {
    const allActions = await indexedDBService.offlineDB.getAll<OfflineAction>('offline_actions');

    const stats = {
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0,
      total: allActions.length
    };

    for (const action of allActions) {
      stats[action.status as keyof typeof stats]++;
    }

    return stats;
  }

  /**
   * Setup network listeners
   */
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {

      this.processQueue().catch(console.error);
    });

    // Start periodic processing when online
    if (navigator.onLine) {
      this.startPeriodicProcessing();
    }

    window.addEventListener('offline', () => {
      this.stopPeriodicProcessing();
    });
  }

  /**
   * Start periodic queue processing
   */
  private startPeriodicProcessing(): void {
    if (this.processingInterval) return;

    this.processingInterval = setInterval(() => {
      if (navigator.onLine) {
        this.processQueue().catch(console.error);
      }
    }, 30000); // Process every 30 seconds
  }

  /**
   * Stop periodic queue processing
   */
  private stopPeriodicProcessing(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
  }

  /**
   * Setup default action executors
   */
  private setupDefaultExecutors(): void {
    // Training session executors
    this.registerExecutor('create_training_session', async (_action) => {
      try {
        // This would integrate with your existing training session service

        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true 
        };
      }
    });

    this.registerExecutor('update_training_session', async (_action) => {
      try {

        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true 
        };
      }
    });

    this.registerExecutor('delete_training_session', async (_action) => {
      try {

        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true 
        };
      }
    });

    // User technique executors
    this.registerExecutor('save_user_technique', async (_action) => {
      try {

        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true 
        };
      }
    });

    // Profile update executor
    this.registerExecutor('update_user_profile', async (_action) => {
      try {

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true
        };
      }
    });

    // Game plan executors
    this.registerExecutor('create_game_plan', async (action) => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');

        // Extract game plan data from action payload
        const gamePlanData = action.payload;

        // Prepare data for Supabase edge function
        const payload = {
          name: gamePlanData.name,
          description: gamePlanData.description,
          flow_data: gamePlanData.flow_data,
          thumbnail: gamePlanData.thumbnail,
          is_public: gamePlanData.is_public || false,
          is_template: gamePlanData.is_template || false
        };

        const { data, error } = await supabase.functions.invoke('create_game_plan', {
          body: JSON.stringify(payload)
        });

        if (error) {
          throw error;
        }

        return { success: true, data };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true
        };
      }
    });

    this.registerExecutor('update_game_plan', async (action) => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');

        // Extract game plan data from action payload
        const gamePlanData = action.payload;

        // Prepare data for Supabase edge function
        const payload = {
          id: gamePlanData.id,
          name: gamePlanData.name,
          description: gamePlanData.description,
          flow_data: gamePlanData.flow_data,
          thumbnail: gamePlanData.thumbnail,
          is_public: gamePlanData.is_public || false
        };

        const { data, error } = await supabase.functions.invoke('update_game_plan', {
          body: JSON.stringify(payload)
        });

        if (error) {
          throw error;
        }

        return { success: true, data };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true
        };
      }
    });

    this.registerExecutor('delete_game_plan', async (action) => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');

        // Extract game plan ID from action payload
        const { id } = action.payload;

        if (!id) {
          throw new Error('Game plan ID is required for deletion');
        }

        // Get current user for ownership verification
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Authentication required to delete game plans');
        }

        // Delete the game plan (RLS policy will ensure user owns it)
        const { error } = await supabase
          .from('game_plans')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) {
          throw error;
        }

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          shouldRetry: true
        };
      }
    });
  }

  /**
   * Shutdown queue processing
   */
  shutdown(): void {
    this.stopPeriodicProcessing();
    this.isProcessing = false;
  }
}

// Export singleton instance
export const offlineActionQueue = new OfflineActionQueue();
